import React, { useState } from 'react';
import { downloadConnectedEmailAttachment } from '@core/api/integrations/integrationsApi';
import { Box, IconButton, Typography, useTheme } from '@ublend-npm/aulaui-core';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { OutlookThreadMessage } from '../types';
import { FileCopy } from '@mui/icons-material';

type AttachmentItem = NonNullable<OutlookThreadMessage['attachments']>[number];

type OutlookAttachmentProps = {
  attachment: AttachmentItem;
};

/**
 * Renders a single attachment row with file icon, name/size, and download button.
 */
const OutlookAttachment = ({ attachment }: OutlookAttachmentProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const theme = useTheme();

  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadError(null);

    try {
      const { data } = await downloadConnectedEmailAttachment(
        'microsoft',
        attachment.messageId,
        attachment.id
      );

      const downloadUrl = data?.downloadUrl;

      if (!downloadUrl) {
        setDownloadError('This attachment could not be downloaded.');
        return;
      }

      const response = await fetch(downloadUrl, {
        method: 'GET',
        credentials: 'omit',
      });

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const contentType = response.headers.get('content-type') || '';
      if (
        contentType.includes('application/json') ||
        contentType.includes('text/html') ||
        contentType.includes('text/plain')
      ) {
        throw new Error(
          'The downloaded response is not a valid file attachment.'
        );
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = attachment.name || 'attachment';
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      anchor.click();

      setTimeout(() => {
        document.body.removeChild(anchor);
        URL.revokeObjectURL(blobUrl);
      }, 1000);
    } catch (error) {
      const message =
        (
          error as {
            response?: {
              data?: {
                message?: string;
                data?: { message?: string; code?: string };
              };
            };
          }
        )?.response?.data?.message ||
        (
          error as {
            response?: {
              data?: { data?: { message?: string; code?: string } };
            };
          }
        )?.response?.data?.data?.message ||
        (error as { message?: string })?.message ||
        'This attachment could not be downloaded.';

      const normalized = message.toLowerCase();
      const friendlyMessage = normalized.includes('shared link')
        ? 'This attachment is a shared link and cannot be downloaded directly. Please open it from Outlook or save it as a real file first.'
        : normalized.includes('reference_attachment')
        ? 'This attachment is a shared link and cannot be downloaded directly. Please open it from Outlook or save it as a real file first.'
        : 'This attachment could not be downloaded right now. Please try again.';

      setDownloadError(friendlyMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      height="60px"
      p="8px"
      sx={{
        border: '1px solid',
        borderColor:
          theme.palette.mode === 'dark'
            ? 'rgba(248, 247, 249, 0.16)'
            : 'rgba(35, 31, 41, 0.16)',
        borderRadius: '4px',
        bgcolor: theme.palette.mode === 'dark' ? '#231F29' : '#F8F7F9',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={40}
        height={40}
        flexShrink={0}
        sx={{
          borderRadius: '8px',
        }}
      >
        <FileCopy sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
      </Box>

      <Box minWidth={0} flex={1}>
        <Typography
          variant="subtitle2"
          fontWeight={500}
          noWrap
          sx={{
            fontSize: '14px',
            lineHeight: '21px',
            letterSpacing: '0.1px',
            color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#0F172A',
          }}
        >
          {attachment.name}
        </Typography>
        {attachment.size ? (
          <Typography variant="body2" color="text.secondary" mt={0.25}>
            {attachment.size}
          </Typography>
        ) : null}
        {downloadError ? (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.5,
              color: theme.palette.error.main,
              lineHeight: 1.4,
            }}
          >
            {downloadError}
          </Typography>
        ) : null}
      </Box>

      <IconButton
        aria-label={`Download ${attachment.name}`}
        onClick={handleDownload}
        disabled={isDownloading}
        sx={{
          width: 40,
          height: 40,
          minWidth: 40,
          minHeight: 40,
          p: 0,
          bgcolor: '#B0BEC5',
          color: theme.palette.mode === 'dark' ? '#231F29' : '#FFFFFF',
          '&:hover': { bgcolor: '#90A4AE' },
        }}
      >
        <FileDownloadIcon sx={{ fontSize: '24px' }} />
      </IconButton>
    </Box>
  );
};

export default OutlookAttachment;
